<template>
  <!-- PRO: a free user's tap opens the upgrade page instead of ticking it (the parent decides). -->
  <button
    type="button"
    role="checkbox"
    class="import-option import-option--check"
    :data-test="testId"
    :aria-checked="checked"
    @click="$emit('toggle', !checked)"
  >
    <span class="import-option__text">
      <span class="import-option__name">
        {{ title }}
        <span v-if="!isPro" class="import-option__badge" :data-test="`${testId}-pro-badge`">{{ $t('shared.pro') }}</span>
      </span>
      <span class="import-option__desc">{{ hint }}</span>
      <span v-if="status === 'matching'" class="import-option__desc" :data-test="`${testId}-status`">{{ $t('import_files.snap_to_paths_progress') }}</span>
      <span v-else-if="status === 'failed'" class="import-option__desc warn" :data-test="`${testId}-status`">{{ $t('import_files.snap_to_paths_preview_failed') }}</span>
    </span>
    <span class="import-check" aria-hidden="true"></span>
  </button>
</template>

<script setup lang="ts">
// A "Snap to paths" check row: one file's (in its settings, under Lines) or every planned-hike
// file's at once (above the list). Looks like the screen's other check rows, plus the PRO
// badge and a status line while matching or when nothing could be matched.
withDefaults(
  defineProps<{
    title: string;
    hint: string;
    checked: boolean;
    isPro: boolean;
    status?: 'idle' | 'matching' | 'failed';
    testId: string;
  }>(),
  { status: 'idle' }
);
defineEmits<{ (e: 'toggle', checked: boolean): void }>();
</script>

<style scoped>
/* The screen's check row (ImportFileSettings.vue), which this sits among. */
.import-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  white-space: normal;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: var(--gray-raised);
  color: var(--white);
  text-align: left;
  cursor: pointer;
}
.import-option:hover {
  background: var(--gray-hover);
}
.import-option__text {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.import-option__name {
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.import-option__badge {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.5;
  padding: 0 5px;
  border-radius: 4px;
  background: var(--yellow);
  color: var(--dark);
}
.import-option__desc {
  font-size: 12px;
  color: var(--text-muted);
}
.import-option__desc.warn {
  color: var(--orange);
}
.import-check {
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid var(--gray-border);
  display: grid;
  place-items: center;
}
.import-option[aria-checked='true'] .import-check {
  background: var(--yellow);
  border-color: var(--yellow);
}
.import-option[aria-checked='true'] .import-check::after {
  content: '';
  width: 11px;
  height: 6px;
  border: solid var(--dark);
  border-width: 0 0 2.5px 2.5px;
  transform: rotate(-45deg) translate(1px, -1px);
}
</style>
